import { createRouter, createWebHistory } from 'vue-router'

import { baseUrl, isDevBuild } from '@/../app.config'
import { hasPrivilege } from '@/auth/authorization'
import { modelNamePluralize, UUID_PATTERN } from '@/utils'
import { resolveModelMetaFromId } from '@/utils/api'

import dataloaderRoutes from './data-loader'
import ontologyRoutes from './ontology'
import {
  authGuard,
  cancelPendingRequests,
  getCloneQParams,
  onLogout,
  redirectGuard,
} from './utils' // router utils
import workflowRoutes from './workflows'

/* Views - Lazy loaded for code splitting */
const LandingView = () => import('@/views/LandingView.vue')
const LoginView = () => import('@/views/LoginView.vue')
const SSOLandingView = () => import('@/views/SSOLandingView.vue')
const WelcomeView = () => import('@/views/WelcomeView.vue')
const SearchView = () => import('@/views/SearchView.vue')
const UserView = () => import('@/views/UserView.vue')
const UserTasksView = () => import('@/views/UserTasksView.vue')
const OfficeAppView = () => import('@/views/OfficeAppView.vue')
const NotFoundView = () => import('@/views/NotFoundView.vue')

/* Listing */
const ListView = () => import('@/views/ListView.vue')

/* Details */
const AssayDetailView = () => import('@/views/detail/AssayDetailView.vue')
const SampleDetailView = () => import('@/views/detail/SampleDetailView.vue')
const StockDetailView = () => import('@/views/detail/StockDetailView.vue')
const VocabDetailView = () => import('@/views/detail/VocabDetailView.vue')
const LabNoteDetailView = () => import('@/views/detail/LabNoteDetailView.vue')
const OrderDetailView = () => import('@/views/detail/OrderDetailView.vue')
const LineageView = () => import('@/views/LineageView.vue')

/* New */
const OrderNewView = () => import('@/views/new/OrderNewView.vue')
const StockNewView = () => import('@/views/new/StockNewView.vue')
const SampleNewView = () => import('@/views/new/SampleNewView.vue')
const BatchImportView = () => import('@/views/BatchImportView.vue')
const BatchEditView = () => import('@/views/BatchEditView.vue')
const ViewFileView = () => import('@/views/ViewFileView.vue')

export const ROUTE_NAMES = {
  HOME: 'landing',
  WELCOME: 'welcome',
  LOGIN: 'login',
}

const routes = [
  ...dataloaderRoutes,
  ...workflowRoutes,
  ...ontologyRoutes,
  {
    path: '/home',
    name: ROUTE_NAMES.HOME,
    component: LandingView,
    meta: { layout: 'BaseLayout', requireAuth: false, noRedirect: true },
  },
  {
    path: '/',
    name: ROUTE_NAMES.WELCOME,
    component: WelcomeView,
    meta: { noRedirect: true },
  },
  {
    path: '/login',
    name: ROUTE_NAMES.LOGIN,
    component: LoginView,
    meta: { layout: 'BaseLayout', requireAuth: false },
  },
  {
    path: '/sso-landing',
    name: 'sso-landing',
    component: SSOLandingView,
    meta: { layout: 'BaseLayout', requireAuth: false, noRedirect: true },
  },
  {
    path: '/logout',
    name: 'logout',
    beforeEnter: onLogout,
  },
  {
    path: '/tasks',
    component: UserTasksView,
    name: 'userTasks',
    props: true,
  },
  {
    path: '/search',
    name: 'search',
    component: SearchView,
  },
  {
    path: `/:id(${UUID_PATTERN})`,
    component: NotFoundView, // never rendered — beforeEnter always redirects
    beforeEnter: async (to) => {
      const id = to.params.id
      try {
        const meta = await resolveModelMetaFromId(id)
        const { appName, modelName, modelType } = meta || {}
        if (!appName || !modelName || !modelType) {
          console.warn('[resolver] Incomplete metadata for id:', id, meta)
          return { name: 'notFound' }
        }
        const pluralModelName = modelNamePluralize(modelName)
        return {
          path: `/${[appName, pluralModelName, modelType, id]
            .map((m) => m.toLowerCase())
            .join('/')}`,
        }
      } catch (error) {
        console.warn('[resolver] Failed to resolve id:', id, error)
        return { name: 'notFound' }
      }
    },
  },
  {
    path: `/lineage/:uuid(${UUID_PATTERN})`,
    name: 'lineage',
    component: LineageView,
    props: true,
  },
  {
    path: '/users/:userUsername',
    component: UserView,
    name: 'userDetail',
    props: true,
  },
  {
    path: `/apps/office/:id(${UUID_PATTERN})`,
    component: OfficeAppView,
    name: 'officeApp',
    props: true,
  },
  ...(isDevBuild
    ? [
        {
          path: '/dev/widget-playground',
          name: 'widgetPlayground',
          component: () => import('@/views/DevWidgetPlayground.vue'),
          meta: { layout: 'div', requireAuth: false },
        },
      ]
    : []),
  {
    path: '/:appName/:modelName/import/edit',
    component: BatchEditView,
    name: 'editTemplateImport',
    props: (route) => ({
      ...route.params,
      context: 'edit',
    }),
  },
  {
    path: '/:appName/:modelName/import',
    component: BatchImportView,
    name: 'batchImport',
    props: true,
  },
  {
    path: `/assays/experiments/:modelType/:id(${UUID_PATTERN})`,
    component: LabNoteDetailView,
    name: 'experimentDetail',
    props: (route) => ({
      ...route.params,
      appName: 'assays',
      modelName: 'experiments',
      ...getCloneQParams(route.query),
    }),
  },
  {
    path: `/assays/assays/:modelType/:id(${UUID_PATTERN})`,
    component: AssayDetailView,
    name: 'assayDetail',
    props: (route) => ({
      ...route.params,
      appName: 'assays',
      modelName: 'assays',
    }),
  },
  {
    path: `/protocols/protocols/:modelType/:id(${UUID_PATTERN})`,
    component: LabNoteDetailView,
    name: 'protocolDetail',
    props: (route) => ({
      ...route.params,
      appName: 'protocols',
      modelName: 'protocols',
      ...getCloneQParams(route.query),
    }),
  },
  {
    path: '/stocks/orders/default/new',
    component: OrderNewView,
    name: 'newOrder',
  },
  {
    path: `/stocks/orders/default/:id(${UUID_PATTERN})`,
    component: OrderDetailView,
    name: 'orderDetail',
    props: (route) => ({
      ...route.params,
      appName: 'stocks',
      modelName: 'orders',
      modelType: 'default',
    }),
  },
  {
    path: '/:appName/:modelName',
    component: ListView,
    name: 'listDefault',
    props: true,
    beforeEnter: (to) => {
      const appName = to.params.appName
      const modelName = to.params.modelName
      const canNav =
        appName === 'vocabularies' ||
        (appName === 'stocks' && modelName === 'orders')
      if (!canNav) return { name: 'notFound' }
    },
  },
  {
    path: '/:appName/:modelName/:modelType',
    component: ListView,
    name: 'list',
    props: (route) => {
      const { ownership, ...filters } = route.query
      return {
        ...route.params,
        filters: Object.keys(filters).length ? filters : null,
        ownership: ownership || null,
      }
    },
  },
  {
    path: `/stocks/samples/:modelType/:id(${UUID_PATTERN})`,
    component: SampleDetailView,
    name: 'sampleDetail',
    props: (route) => ({
      ...route.params,
      appName: 'stocks',
      modelName: 'samples',
    }),
  },
  {
    path: `/vocabularies/:modelName/:modelType/:id(${UUID_PATTERN})`,
    component: VocabDetailView,
    name: 'vocabDetail',
    props: (route) => ({
      modelName: route.params.modelName,
      modelType: route.params.modelType,
      id: route.params.id,
    }),
  },
  {
    path: `/:appName/:modelName/:modelType/:id(${UUID_PATTERN})`,
    component: StockDetailView,
    name: 'detail',
    props: true,
  },
  {
    path: '/stocks/samples/:modelType/new',
    component: SampleNewView,
    name: 'sampleNew',
    props: (route) => ({
      ...route.params,
      ...getCloneQParams(route.query),
    }),
  },
  {
    path: '/:appName/:modelName/:modelType/new',
    component: StockNewView,
    name: 'stockNew',
    beforeEnter: (to, from, next) => {
      const { appName, modelName } = to.params
      if (hasPrivilege(appName, modelName, 'add')) next()
      else next({ name: 'notFound' })
    },
    props: (route) => ({
      ...route.params,
      ...getCloneQParams(route.query),
    }),
  },
  {
    path: '/view',
    name: 'viewFile',
    component: ViewFileView,
    meta: { layout: 'div' },
    // app/model drive the signed-download endpoint. If a link omits them
    // (e.g. a bare /view?id=…), resolve them from the id before rendering.
    beforeEnter: async (to) => {
      const { id, app, model } = to.query
      if (!id || (app && model)) return
      try {
        const meta = await resolveModelMetaFromId(id)
        if (meta?.appName && meta?.modelName) {
          return {
            name: 'viewFile',
            replace: true,
            query: {
              ...to.query,
              app: meta.appName,
              model: modelNamePluralize(meta.modelName),
            },
          }
        }
      } catch (error) {
        console.warn(
          '[viewFile] could not resolve app/model for id:',
          id,
          error,
        )
      }
    },
    props: (route) => ({ ...route.query }),
  },
  {
    path: '/404',
    name: 'notFound',
    component: NotFoundView,
    props: (route) => ({ id: route.query.id }),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404',
  },
]

const router = createRouter({
  history: createWebHistory(baseUrl),
  routes,
})
router.beforeEach(authGuard)
router.beforeEach(redirectGuard)
router.beforeEach(cancelPendingRequests)

export default router
